/* eslint-disable react-hooks/rules-of-hooks */
import CarsTableDamageCell from '@components/cars-table-next/cells/DamageCell';
import { useCarUpdate } from '@components/cars-table-next/useCarUpdate';
import { Activity, ActivityList, DocView, DocViewTitle, DocViewTrigger } from '@components/ui';
import { Tooltip } from '@material-ui/core';
import { green, grey } from '@material-ui/core/colors';
import { Apps } from '@material-ui/icons';
import { get, meanBy } from 'lodash';
import { DateTime } from 'luxon';
import { lighten } from 'polished';
import { ChangeEventHandler, useRef } from 'react';
import { OverlayContainer, useButton, useOverlayPosition, useOverlayTrigger } from 'react-aria';
import { Trans, useTranslation } from 'react-i18next';
import { useOverlayTriggerState } from 'react-stately';
import { getActivities } from 'src/modules/activity';
import { COUNTRY_ORIGIN_FILTER_COUNTRIES, IconType, fallbackIcon, getIcon } from 'src/modules/data';
import {
  ConditionalFormattingType,
  FirstCallType,
  MonetaryAmount,
  NormalizedColor,
  SourceType,
  ValidityType,
} from 'src/modules/generated/api';
import { Option, tr } from 'src/modules/i18n-helpers';
import {
  colors,
  countryCodeToOptions,
  engineFuelDetails,
  engineFuelOld,
  equipments,
  getLabel,
  promotions,
  sourceOriginFilter,
  sources,
} from 'src/modules/labels';
import { openAuthenticatedPdf } from 'src/modules/pdf-helpers';
import { formatModelNames } from 'src/modules/string-helpers';
import { palettePrimaryMain } from 'src/setup/css-vars';
import i18n from 'src/setup/i18n';
import theme from 'src/setup/theme';
import { Potential } from 'src/types/potential';

import type { BidsTableNextItem, CarsTableNextItem, ExportDef, StrictColumnDef } from '../types';

import { useCurrency } from '../../../hooks/useCurrency';
import { useUser } from '../../../hooks/useUser';
import { ActivityType } from '../../../modules/activity';
import { SupportedCurrencyCode, makeMonetaryAmount } from '../../../modules/currency';
import humanizeDurationInstance from '../../../modules/humanize-duration';
import UserRoles from '../../../types/UserRoles';
import {
  AvatarGroupCell,
  LeadIconCell,
  MinMaxCell,
  NumericCell,
  NumericCellFallback,
  PackageBodyTypesCell,
  PackageBrandCell,
  PackageFuelCell,
  RatingCell,
  TextCell,
  TextCellFallback,
  TimestampCell,
  getAvatarCount,
} from '../cells';
import { CarImage } from '../cells/CarImage';
import { PriceChangeAbsoluteCell, PriceChangeRelativeCell } from '../cells/PriceChangeCell';
import { TrendCell } from '../cells/TrendCell';
import { ColumnSize, ExportType } from '../constants';
import { currencyFormat, formatCO2, formatMileage, percentFormat } from '../format';
import { isDefined } from '../lib';

import { bidColumns } from './bid-columns';
import { createPotentialColumn } from './common/templates';
import { externalColumns } from './external-columns';
import { IconGroup } from './IconGroup';
import { buildPromotionAvatars } from './lib';
import { taxColumns } from './tax-columns';

const getMinutesFromNow = (date: string | number | Date): number => (Date.now() - +new Date(date)) / 1000 / 60;

const getMonthsFromNow = (date: string | number | Date): number => getMinutesFromNow(date) / 60 / 24 / 30.437;

// The text color vars must stay on this cell-container style object: TableNext's getCellProps spreads
// the conditional-formatting styles over it, and lib.ts's getBackgroundStyles clears
// --text_cell__text_color___text_solo so the highlight gets disabled whenever a format matches.
// Moving them onto a descendant (e.g. the cell's own style prop) would defeat that override.
// palettePrimaryMain is defined per-theme in setup/theme.ts, so this follows the active branding.
const highlightCellStyle = {
  '--text_cell__text_color___text': palettePrimaryMain,
  '--text_cell__text_color___text_solo': palettePrimaryMain,
  fontWeight: 'bold',
};

const visualExportTypeOptions: ExportDef['typeOptions'] = [
  { value: ExportType.textAndIcons, label: 'carsTableNext.export.exportTypes.textAndIcons' },
  { value: ExportType.textOnly, label: 'carsTableNext.export.exportTypes.textOnly' },
  { value: ExportType.iconsOnly, label: 'carsTableNext.export.exportTypes.iconsOnly' },
];

const optionsToExportValueTable = (options: Readonly<Option[]>) =>
  options.map((option) => ({ name: option.value, label: tr(option.label) }));

export const columns = (enableSorting: boolean = true): StrictColumnDef<CarsTableNextItem>[] => [
  {
    id: 'car',
    accessorKey: 'carId',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show brand icons with counts and models
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.brands) {
        return (
          <PackageBrandCell
            brands={original.packageAggregation.brands}
            models={original.packageAggregation.models}
            totalCars={original.packageAggregation.nrOfCarsInPackage}
          />
        );
      }
      // Non-package bid: existing rendering
      return (
        <LeadIconCell
          key={`${original.id}-leadicon`}
          text={original?.model}
          supportText={original?.vin}
          startDate={original.dateStart}
          endDate={original.dateEnd}
          recommended={original.recommended}
          icon={
            <CarImage
              bidStateInfo={original?.bidStateInfo}
              imageThumbnailLink={original?.imageThumbnailLink}
              expired={original?.validity === ValidityType.Expired}
              vin={original.vin}
              watchlist={original.watchlistIds ?? []}
              key={`${original.id}-carimage`}
            />
          }
        />
      );
    },
    size: ColumnSize.lg,
    meta: {
      fallback: () => <TextCellFallback textWidth="80%" supportTextWidth="50%" withStartAdornment />,
      export: {
        exports: [
          {
            key: 'imageLinks',
            label: 'carsTableNext.car.imageLink_plural',
          },
          {
            key: 'model',
            label: 'carsTableNext.car.model',
          },
          {
            key: 'modelcode',
            label: 'carsTableNext.car.modelCode',
          },
          {
            key: 'normalized.model',
            label: 'carsTableNext.car.normalized.model',
          },
          {
            key: 'normalized.brand',
            label: 'carsTableNext.car.normalized.brand',
          },
          {
            key: 'normalized.equipmentLine',
            label: 'carsTableNext.car.normalized.equipmentLine',
          },
          {
            key: 'normalized.version',
            label: 'carsTableNext.car.normalized.version',
          },
          {
            key: 'vin',
            label: 'carsTableNext.car.vin',
          },
          {
            key: 'carId',
            label: 'carsTableNext.car.carId',
          },
          {
            key: 'packageId',
            label: 'carsTableNext.car.packageId',
          },
          {
            key: 'zip',
            label: 'carsTableNext.car.zip',
          },
        ],
      },
      conditionalFormat: {
        key: ConditionalFormattingType.Model,
        getValue: ({ row }) => row.original.model,
      },
      tableCellProps: ({ row }) => ({
        style: {
          background: row.original.isNew ? '#b8e0b9' : undefined,
          transition: 'background-color 300ms ease-in-out',
        },
      }),
    },
    enableSorting: false,
  },
  {
    id: 'rating',
    accessorKey: 'rating',
    cell: (props) => {
      const {
        getValue,
        row: { original },
      } = props;

      // Package bid: show placeholder
      if ((original as BidsTableNextItem).isPackageBid) {
        return <TextCell text="-" />;
      }

      const { t } = useTranslation();
      const { mutate: updateCar } = useCarUpdate();
      const overlayState = useOverlayTriggerState({});

      const triggerRef = useRef<HTMLDivElement>(null);
      const overlayRef = useRef(null);

      const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef);

      const { buttonProps } = useButton(triggerProps, triggerRef);

      const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: triggerRef,
        overlayRef,
        placement: 'right top',
        offset: 6,
        isOpen: overlayState.isOpen,
      });

      const handleChangeRating: ChangeEventHandler<{}> = (event) => {
        const { value } = event.target as HTMLInputElement;
        const rating = parseInt(value, 10);
        updateCar({ ...original, rating: rating === original.rating ? 0 : rating });
      };

      const activities = getActivities(original.individualizations ?? []);
      const ratings = activities.filter((activity) => activity.type === ActivityType.rating);

      return (
        <>
          {activities.length > 0 && (
            <DocViewTrigger
              {...buttonProps}
              ref={triggerRef}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          )}
          {overlayState.isOpen && (
            <OverlayContainer>
              <DocView
                {...overlayProps}
                {...positionProps}
                ref={overlayRef}
                title={({ titleProps }) => (
                  <DocViewTitle {...titleProps} gutterBottom>
                    {t('carsTableNext.cells.rating.externalDocView.title')}
                  </DocViewTitle>
                )}
                isOpen={overlayState.isOpen}
                onClose={overlayState.close}
              >
                <ActivityList spacing={2}>
                  {activities.map((activity) => (
                    <Activity key={activity.id} {...activity} createdAt={activity.createdAt ?? '-'} />
                  ))}
                </ActivityList>
              </DocView>
            </OverlayContainer>
          )}
          <RatingCell
            value={getValue<CarsTableNextItem['rating']>() ?? 0}
            supportText={original.userComment}
            onChange={handleChangeRating}
            style={{
              pointerEvents: 'all',
            }}
            name={`rating-${original.carId}`}
            comparisonRating={ratings.length > 0 && meanBy(ratings, (x) => x.value)}
            comparisonRatingCount={ratings.length}
          />
        </>
      );
    },
    size: ColumnSize.sm,
    enableSorting,
    meta: {
      // prevent row-click & focus
      tableCellProps: {
        onClick: (event) => event.stopPropagation(),
        style: {
          cursor: 'default',
        },
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
      filter: ['ratingMin', 'anyOtherUserRatingMin', 'hasUserComment', 'hasAnyOtherUserComment'],
      export: {
        exports: [
          {
            key: 'rating',
            label: 'carsTableNext.car.rating',
          },
          {
            key: 'userComment',
            label: 'carsTableNext.car.userComment',
          },
        ],
      },
    },
  },
  {
    id: 'source',
    accessorKey: 'source',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show package ID and source
      if ((original as BidsTableNextItem).isPackageBid) {
        return (
          <TextCell
            text={getLabel(sources, original.packageAggregation?.source as string) ?? '-'}
            supportText={
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 'smaller' }}>
                <Tooltip title={`${tr('common.package')}-${tr('carsTable.ID')}`}>
                  <Apps fontSize="small" />
                </Tooltip>{' '}
                {original.packageAggregation?.packageId ?? '-'}
              </div>
            }
          />
        );
      }
      return (
        <TextCell
          text={getLabel(sources, props.getValue<CarsTableNextItem['source']>() as string) ?? '-'}
          supportText={<IconGroup call={original.firstCallType} virtual={original.virtualSources as SourceType[]} />}
        />
      );
    },
    size: ColumnSize.xs,
    minSize: 120,
    meta: {
      filter: ['source'],
    },
    enableSorting,
  },
  {
    id: 'originSource',
    accessorKey: 'originSource',
    cell: (props) => (
      <TextCell
        text={getLabel(sourceOriginFilter, props.getValue<CarsTableNextItem['originSource']>() as string) ?? '-'}
      />
    ),
    size: ColumnSize.md,
    meta: {
      filter: ['originSource'],
      columnVisible: false,
      isCustomerModeHidden: true,
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting,
  },
  {
    id: 'dateStart',
    accessorKey: 'dateStart',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: get dateStart from cars[0] (bids table) or directly from the row (cars table)
      if ((original as BidsTableNextItem).isPackageBid) {
        const { cars } = original as BidsTableNextItem;
        return <TimestampCell timestamp={cars?.[0]?.dateStart ?? original.dateStart} />;
      }
      return <TimestampCell timestamp={props.getValue<CarsTableNextItem['dateStart']>()} />;
    },
    size: ColumnSize.xs,
    meta: {
      fallback: () => <TextCellFallback textWidth="80%" supportTextWidth="60%" />,
      filter: ['startDateFilter'],
    },
  },
  {
    id: 'dateEnd',
    accessorKey: 'dateEnd',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: get dateEnd from cars[0] (bids table) or directly from the row (cars table)
      if ((original as BidsTableNextItem).isPackageBid) {
        const { cars } = original as BidsTableNextItem;
        return <TimestampCell timestamp={cars?.[0]?.dateEnd ?? original.dateEnd} />;
      }
      return <TimestampCell timestamp={props.getValue<CarsTableNextItem['dateEnd']>()} />;
    },
    size: ColumnSize.xs,
    meta: {
      fallback: () => <TextCellFallback textWidth="80%" supportTextWidth="60%" />,
      filter: ['endDateFilter'],
    },
  },
  {
    id: 'highlights',
    accessorKey: 'equipmentKeys',
    cell: ({ row: { original }, column }) => {
      const { data: user } = useUser();

      // Package bid: show country icons & highlights
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation) {
        const countryItems = Object.entries(original.packageAggregation.countries || {}).map(([country, count]) => ({
          key: country,
          src: getIcon(IconType.COUNTRY, country),
          helpText: `${country} (${count})`,
        }));

        const equipmentItems = Object.entries(original.packageAggregation.equipmentKeys || {}).map(([, equipment]) => ({
          key: equipment,
          src: getIcon(IconType.EQUIPMENT, equipment),
          helpText: getLabel(equipments, equipment),
          style: {
            background: equipment === 'WARRANTY_EXTENSION' ? green[200] : undefined,
          },
        }));

        const promoItems = buildPromotionAvatars(
          Object.keys(original.packageAggregation.promotions || {}),
          user?.dealer?.country,
        );

        return (
          <AvatarGroupCell
            AvatarProps={{
              imgProps: {
                style: {
                  width: 'calc(100% + 2px)',
                  height: 'calc(100% + 2px)',
                },
              },
            }}
            items={[...countryItems, ...promoItems, ...equipmentItems]}
            max={getAvatarCount(column.getSize(), 2)}
          />
        );
      }

      const primaryAssessmentLink = original.assessmentDocuments?.primary;

      return (
        <AvatarGroupCell
          AvatarProps={{
            imgProps: {
              style: {
                // strip icon border
                width: 'calc(100% + 2px)',
                height: 'calc(100% + 2px)',
              },
            },
          }}
          items={[
            !!primaryAssessmentLink && {
              key: 'assessmentLink',
              src: '/images/assessments/default.svg',
              onClick: () => openAuthenticatedPdf(primaryAssessmentLink!),
              style: { borderColor: theme.palette.primary.main, cursor: 'pointer' },
              helpText: 'carsTableNext.car.assessmentLink',
            },
            !!original.colorId && {
              key: 'colorId',
              src:
                original.colorId !== `${NormalizedColor.Unknown}`
                  ? getIcon(IconType.COLOR, original.colorId)
                  : fallbackIcon,
              helpText: getLabel(colors, original.color ?? original.colorId),
            },
            {
              key: 'countryOrigin',
              src: getIcon(IconType.COUNTRY, original.countryOrigin ?? 'EU'),
              helpText: 'carsTableNext.car.countryOrigin',
            },
            !!original.virtualSources &&
              original.virtualSources.includes(SourceType.Vvj) && {
                key: 'vvj',
                src: getIcon(IconType.VIRTUAL_SOURCE, SourceType.Vvj),
                helpText: getLabel(sources, SourceType.Vvj),
              },
            !!original.firstCallType && {
              key: 'firstCallType',
              src: getIcon(
                IconType.VIRTUAL_SOURCE,
                original.firstCallType === FirstCallType.Own
                  ? SourceType.RealFirstCallOwn
                  : SourceType.RealFirstCallOther,
              ),
              helpText: getLabel(
                sources,
                original.firstCallType === FirstCallType.Own
                  ? SourceType.RealFirstCallOwn
                  : SourceType.RealFirstCallOther,
              ),
            },
            ...buildPromotionAvatars(original.promotions, user?.dealer?.country),
            ...(original.equipmentKeys?.map((equipmentKey: string) => ({
              key: equipmentKey,
              src: getIcon(IconType.EQUIPMENT, equipmentKey),
              helpText: getLabel(equipments, equipmentKey),
              style: {
                background: equipmentKey === 'WARRANTY_EXTENSION' ? green[200] : undefined,
              },
            })) ?? []),
          ].filter(isDefined)}
          max={getAvatarCount(column.getSize(), 2)}
        />
      );
    },
    size: ColumnSize.md,
    meta: {
      export: {
        key: 'equipmentKeys',
        typeOptions: visualExportTypeOptions,
        exports: [
          {
            key: 'countryOrigin',
            label: 'carsTableNext.car.countryOrigin',
            valueLabels: optionsToExportValueTable(
              countryCodeToOptions(COUNTRY_ORIGIN_FILTER_COUNTRIES, i18n.language).sort((a, b) =>
                a.label.localeCompare(b.label, i18n.language),
              ),
            ),
          },
          {
            key: 'colorId',
            label: 'carsTableNext.car.colorId',
          },
          {
            key: 'color',
            label: 'carsTableNext.car.color',
          },
          {
            key: 'equipmentKeys',
            label: 'carsTableNext.car.equipmentKeys',
            valueLabels: optionsToExportValueTable(equipments),
          },
          {
            key: 'assessmentLink',
            label: 'carsTableNext.car.assessmentLink',
          },
          {
            key: 'firstCallType',
            label: 'carsTableNext.car.firstCallType',
          },
          {
            key: 'virtualSources',
            label: 'carsTableNext.car.virtualSource',
          },
        ],
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
      filter: ['equipments'],
      conditionalFormat: ConditionalFormattingType.Highlights,
    },
    enableSorting: false,
  },
  {
    id: 'firstRegistration',
    accessorKey: 'firstRegistration',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show min/max
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.firstRegistration) {
        const { min, max, avg } = original.packageAggregation.firstRegistration;
        const formatDate = (date: string) => DateTime.fromJSDate(new Date(date)).toFormat('MM/yy');
        return (
          <MinMaxCell
            min={min ? formatDate(min) : null}
            max={max ? formatDate(max) : null}
            avg={avg ? formatDate(avg) : null}
          />
        );
      }
      return (
        <TimestampCell
          timestamp={props.getValue<CarsTableNextItem['firstRegistration']>()}
          format="MM/yy"
          displayDiff={false}
        />
      );
    },
    minSize: ColumnSize.xxs,
    size: ColumnSize.xxs,
    meta: {
      fallback: () => <NumericCellFallback textWidth="70%" />,
      filter: ['registrationDate'],
      conditionalFormat: {
        key: ConditionalFormattingType.FirstRegistration,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: use min date for conditional formatting
          if ((original as BidsTableNextItem).isPackageBid) {
            const minDate = original.packageAggregation?.firstRegistration?.min;
            return minDate && getMonthsFromNow(minDate);
          }
          return original.firstRegistration && getMonthsFromNow(original.firstRegistration);
        },
      },
    },
  },
  {
    id: 'mileage',
    accessorKey: 'mileage',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show min/max
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.mileage) {
        const { min, max, average } = original.packageAggregation.mileage;
        return (
          <MinMaxCell
            min={min != null ? formatMileage(min) : null}
            max={max != null ? formatMileage(max) : null}
            avg={average != null ? formatMileage(average) : null}
          />
        );
      }
      return <NumericCell value={props.getValue<CarsTableNextItem['mileage']>() ?? '-'} format={formatMileage} />;
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['mileage'],
      conditionalFormat: {
        key: ConditionalFormattingType.Mileage,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: use max mileage for conditional formatting
          if ((original as BidsTableNextItem).isPackageBid) {
            return original.packageAggregation?.mileage?.max;
          }
          return original.mileage;
        },
      },
    },
    size: ColumnSize.xs,
  },
  {
    id: 'promotions',
    accessorKey: 'promotions',
    cell: (props) => {
      const { original } = props.row;
      // Get promo keys - from packageAggregation for package bids, from promotions array otherwise
      const promoKeys = (original as BidsTableNextItem).isPackageBid
        ? Object.keys(original.packageAggregation?.promotions || {})
        : (props.getValue<CarsTableNextItem['promotions']>() ?? []);

      const filteredPromos = promoKeys.filter((promo) => promo !== 'THERMO' && promo !== 'VVJ');

      return (
        <AvatarGroupCell
          items={filteredPromos.map((promotionKey: string) => ({
            key: promotionKey,
            src: getIcon(IconType.PROMOTION, promotionKey),
            imgProps: {
              style: {
                // strip icon border
                width: 'calc(100% + 2px)',
                height: 'calc(100% + 2px)',
              },
            },
            helpText: getLabel(promotions, promotionKey),
            alt: 'Promotion',
          }))}
          max={getAvatarCount(props.column.getSize(), 2)}
        />
      );
    },
    meta: {
      export: {
        typeOptions: visualExportTypeOptions,
        valueLabels: optionsToExportValueTable(promotions),
      },
      columnVisible: false,
      fallback: () => <TextCellFallback textWidth="80%" />,
      filter: ['promotions'],
      conditionalFormat: {
        key: ConditionalFormattingType.Promotions,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: get promo keys (promotions is { [promoType]: carIds[] })
          if ((original as BidsTableNextItem).isPackageBid) {
            const promoObj = original.packageAggregation?.promotions;
            return promoObj ? Object.keys(promoObj) : undefined;
          }
          return original.promotions;
        },
      },
    },
    enableSorting: false,
  },
  {
    id: 'priceDamage',
    accessorKey: 'priceDamage',
    cell: (props) => {
      const { format: formatCurrency, convert } = useCurrency();
      const {
        getValue,
        column,
        row: { original },
      } = props;
      // Package bid: show aggregated priceDamage min/max or sum
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.priceDamage) {
        const { min, max, sum, avg } = original.packageAggregation.priceDamage;

        if (min?.amount != null && min.currency && max?.amount != null && max.currency) {
          return (
            <MinMaxCell
              min={formatCurrency(convert(min as MonetaryAmount, {}), {})}
              max={formatCurrency(convert(max as MonetaryAmount, {}), {})}
              avg={formatCurrency(convert(avg as MonetaryAmount, {}), {})}
            />
          );
        }

        if (sum?.amount != null && sum.currency) {
          return (
            <NumericCell
              value={makeMonetaryAmount(sum.amount, sum.currency as SupportedCurrencyCode)}
              prefix={<span style={{ opacity: 0.6 }}>Σ </span>}
            />
          );
        }
        return <TextCell text="-" />;
      }
      const value = getValue<CarsTableNextItem['priceDamage']>();
      return (
        <CarsTableDamageCell
          car={original}
          maxIcons={getAvatarCount(column.getSize(), 1)}
          priceDamage={makeMonetaryAmount(value?.value ?? 0, value?.unit as SupportedCurrencyCode)}
        />
      );
    },
    meta: {
      conditionalFormat: [
        {
          key: ConditionalFormattingType.PriceDamage,
          getValue: (cell) => {
            const { original } = cell.row;
            // Package bid: use avg for conditional formatting threshold comparison
            if ((original as BidsTableNextItem).isPackageBid) {
              return original.packageAggregation?.priceDamage?.avg;
            }
            return original.priceDamage;
          },
        },
        {
          key: ConditionalFormattingType.DamageType,
          getValue: (cell) => {
            const { original } = cell.row;
            // Package bid: no damage type aggregation available
            if ((original as BidsTableNextItem).isPackageBid) {
              return undefined;
            }
            return original.damageType;
          },
        },
      ],
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['priceDamage', 'damageType'],
    },
  },
  {
    id: 'normalized.model',
    accessorKey: 'normalized.model',
    cell: (props) => {
      const {
        row: { original },
      } = props;
      const value = original.normalized;
      return <TextCell text={formatModelNames(value?.model ?? '')} />;
    },
    meta: {
      filter: ['normalized.model'],
      fallback: () => <TextCellFallback textWidth="80%" />,
      columnVisible: true,
      export: { enabled: false },
    },
    size: ColumnSize.md,
    enableSorting,
  },
  {
    id: 'normalized.fuelDetail',
    accessorFn: (car) => car?.normalized?.fuelDetail ?? car?.normalized?.fuel,
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show engine fuel counts
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.engineFuel) {
        return <PackageFuelCell fuels={original.packageAggregation.engineFuel} />;
      }

      const fuelDetail = original?.normalized?.fuelDetail;
      const fuel = original?.normalized?.fuel;

      let fuelLabel = '-';

      if (fuelDetail) {
        fuelLabel = getLabel(engineFuelDetails, fuelDetail) ?? '-';
      } else if (fuel) {
        fuelLabel = getLabel(engineFuelOld, fuel) ?? '-';
      }

      return <TextCell text={fuelLabel} />;
    },
    meta: {
      conditionalFormat: {
        key: ConditionalFormattingType.NormalizedFuelDetail,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: use the most common fuel type
          if ((original as BidsTableNextItem).isPackageBid) {
            const engineFuel = original.packageAggregation?.engineFuel;
            if (engineFuel) {
              const entries = Object.entries(engineFuel);
              if (entries.length > 0) {
                // Return fuel type with highest count
                const [mostCommon] = entries.sort((a, b) => b[1] - a[1]);
                return mostCommon[0];
              }
            }
            return undefined;
          }
          return original?.normalized?.fuelDetail ?? original?.normalized?.fuel;
        },
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
      filter: ['normalizedFuelDetail'],
      columnVisible: false,
    },
    enableSorting,
  },

  {
    id: 'batteryCapacityGross',
    accessorKey: 'batteryCapacityGross',
    cell: (props) => {
      const { original } = props.row;
      // Package bid: show min/max/avg kWh from aggregation
      if ((original as BidsTableNextItem).isPackageBid) {
        const stats = original.packageAggregation?.batteryCapacityGross;
        if (stats) {
          return (
            <MinMaxCell
              min={stats.min != null ? `${stats.min} kWh` : null}
              max={stats.max != null ? `${stats.max} kWh` : null}
              avg={stats.average != null ? `${Math.round(stats.average)} kWh` : null}
            />
          );
        }
        return <TextCell text="-" />;
      }
      const value = props.getValue<number>();
      return <NumericCell value={value != null ? `${value} kWh` : '-'} />;
    },
    meta: {
      isNumeric: true,
      columnVisible: false,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['batteryCapacityGross'],
      export: { enabled: false },
    },
    enableSorting: false,
  },
  {
    id: 'engineCo2Nedc',
    accessorKey: 'engineCo2Nedc',
    cell: (props) => {
      const {
        getValue,
        row: { original },
      } = props;
      // Package bid: show min-max with unit
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.engineCo2Nedc) {
        const { min, max, average } = original.packageAggregation.engineCo2Nedc;
        return (
          <MinMaxCell
            min={min != null ? formatCO2(min, false) : null}
            max={max != null ? formatCO2(max, false) : null}
            avg={average != null ? formatCO2(average, false) : null}
          />
        );
      }
      const value = getValue<CarsTableNextItem['engineCo2Nedc']>();
      return (
        <NumericCell
          value={value ?? '-'}
          format={(v) => formatCO2(v, !!original.co2ApproximationMethod)}
          helperText={!!original.co2ApproximationMethod && !!value && 'carsTable.hints.approxCo2'}
        />
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['engineCo2Nedc'],
      conditionalFormat: {
        key: ConditionalFormattingType.Co2Nefz,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: use max CO2 for conditional formatting
          if ((original as BidsTableNextItem).isPackageBid) {
            return original.packageAggregation?.engineCo2Nedc?.max;
          }
          return original.engineCo2Nedc;
        },
      },
    },
    enableSorting,
  },
  {
    id: 'engineCo2Wltp',
    accessorKey: 'engineCo2Wltp',
    cell: (props) => {
      const {
        getValue,
        row: { original },
      } = props;
      // Package bid: show min-max with unit
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.engineCo2Wltp) {
        const { min, max, average } = original.packageAggregation.engineCo2Wltp;
        return (
          <MinMaxCell
            min={min != null ? formatCO2(min, false) : null}
            max={max != null ? formatCO2(max, false) : null}
            avg={average != null ? formatCO2(average, false) : null}
          />
        );
      }
      const value = getValue<CarsTableNextItem['engineCo2Wltp']>();
      return (
        <NumericCell
          value={value ?? '-'}
          format={(v) => formatCO2(v, !!original.co2ApproximationMethod)}
          helperText={!!original.co2ApproximationMethod && !!value && 'carsTable.hints.approxCo2'}
        />
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['engineCo2Wltp'],
      conditionalFormat: {
        key: ConditionalFormattingType.Co2Wltp,
        getValue: ({ row }) => {
          const { original } = row;
          // Package bid: use max CO2 for conditional formatting
          if ((original as BidsTableNextItem).isPackageBid) {
            return original.packageAggregation?.engineCo2Wltp?.max;
          }
          return original.engineCo2Wltp;
        },
      },
    },
    enableSorting,
  },
  {
    id: 'shape',
    accessorKey: 'normalized.bodyType',
    cell: (props) => {
      const { t } = useTranslation();
      const { original } = props.row;
      // Package bid: show body type distribution from aggregation
      if ((original as BidsTableNextItem).isPackageBid) {
        const bodyTypesMap = original.packageAggregation?.bodyTypes;
        if (bodyTypesMap && Object.keys(bodyTypesMap).length > 0) {
          return <PackageBodyTypesCell types={bodyTypesMap} />;
        }
        return <TextCell text="-" />;
      }
      const value = props.getValue<string>();
      return <TextCell text={value ? (t(`car.bodyTypes.${value}` as any) as string) : '-'} />;
    },
    meta: {
      columnVisible: false,
      fallback: () => <TextCellFallback textWidth="80%" />,
      filter: ['bodyType'],
      export: { enabled: false },
    },
    enableSorting: false,
  },
  {
    id: 'firstCallDealer',
    accessorKey: 'firstCallDealer',
    cell: (props) => {
      const {
        row: { original },
      } = props;
      const value = original.firstCallDealer;
      return <TextCell text={value?.dealer?.id ?? ''} supportText={value?.name ?? ''} />;
    },
    meta: {
      fallback: () => <TextCellFallback textWidth="80%" />,
      roles: [UserRoles.ADMIN],
      columnVisible: false,
    },
    size: ColumnSize.md,
  },
  {
    id: 'firstCallDealerAssignedKvps',
    accessorKey: 'firstCallDealer.assignedKvps',
    cell: (props) => {
      const {
        row: { original },
      } = props;
      const value = original.firstCallDealer;
      const kvps = value ? (value?.assignedKvps ?? []) : [];
      return <TextCell text={kvps.join(', ')} />;
    },
    meta: {
      fallback: () => <TextCellFallback textWidth="80%" />,
      roles: [UserRoles.ADMIN],
      columnVisible: false,
      export: {
        key: 'firstCallDealer.assignedKvps',
      },
    },
    size: ColumnSize.md,
  },
  ...taxColumns,
  {
    id: 'onlineTimeInMinutes',
    accessorKey: 'onlineTimeInMinutes',
    cell: (props) => {
      const {
        getValue,
        row: { original },
      } = props;
      let value;
      // Package bid: get Value from Pgk-Aggr.
      if ((original as BidsTableNextItem).isPackageBid && original.packageAggregation?.onlineTimeInMinutes) {
        value = original.packageAggregation.onlineTimeInMinutes;
      } else {
        value = getValue<CarsTableNextItem['onlineTimeInMinutes']>();
      }
      return <TextCell text={value ? humanizeDurationInstance(value * 60 * 1000) : '-'} />;
    },
    meta: {
      conditionalFormat: ConditionalFormattingType.DateOnlineTimeInMinutes,
      fallback: () => <TextCellFallback textWidth="80%" />,
      roles: [UserRoles.ADMIN],
      columnVisible: false,
      isCustomerModeHidden: true,
    },
    size: ColumnSize.xs,
    enableSorting,
  },
  {
    id: 'numAuctions',
    accessorKey: 'numAuctions',
    cell: (props) => {
      const {
        row: { original },
      } = props;
      // Package bid: use numAuctions from car directly
      const value = (original as BidsTableNextItem).isPackageBid
        ? original.numAuctions
        : props.getValue<CarsTableNextItem['numAuctions']>();
      return <NumericCell value={value ?? '-'} />;
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="2ch" />,
      conditionalFormat: ConditionalFormattingType.NumAuctions,
      filter: ['numAuctions'],
      isCustomerModeHidden: true,
    },
    size: ColumnSize.xs,
  },
  {
    id: 'marketingChannel',
    accessorKey: 'marketingChannel',
    cell: (props) => {
      const { t } = useTranslation();
      const { original } = props.row;

      // Package bid: get marketingChannel from cars[0] (bids table) or directly from the row (cars table)
      const channels = (original as BidsTableNextItem).isPackageBid
        ? ((original as BidsTableNextItem).cars?.[0]?.marketingChannel ?? original.marketingChannel)
        : props.getValue<CarsTableNextItem['marketingChannel']>();

      return (
        <TextCell
          text={channels
            ?.map((channel) => t(`carsTableNext.filter.marketingChannels.${channel}` as any) as string)
            .join(', ')}
        />
      );
    },
    meta: {
      filter: ['marketingChannel'],
    },
    enableSorting,
  },
  {
    id: `potential.${Potential.base}`,
    accessorFn: (car) => get(car, ['potential', Potential.base]),
    cell: (props) => {
      const { original } = props.row;
      // Package bid: use packageAggregation.potential.base, no marketing channel support text
      if ((original as BidsTableNextItem).isPackageBid) {
        const value = get(original, ['packageAggregation', 'potential', Potential.base]);
        return <NumericCell value={value ?? '-'} format={percentFormat.format} />;
      }
      return (
        <NumericCell
          value={props.getValue<MonetaryAmount | number | undefined>() ?? '-'}
          // format only receives percentage formats as MonetaryAmounts are resolved by NumericCell
          format={percentFormat.format}
          supportText={original.marketingChannel?.reduce<JSX.Element[]>(
            (res, channel, index) => [
              ...res,
              <Tooltip
                key={channel}
                title={
                  <Trans
                    i18nKey={`carsTableNext.car.marketingChannel.${channel}`}
                    style={{
                      display: 'flex',
                      alignItems: 'left',
                    }}
                  />
                }
                arrow
              >
                <span>
                  <span
                    style={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      cursor: 'help',
                    }}
                  >
                    <Trans i18nKey={`carsTableNext.car.marketingChannel.${channel}_abbr`} />
                  </span>
                  {original.marketingChannel?.at(index + 1) ? ', ' : null}
                </span>
              </Tooltip>,
              <TrendCell
                key={`1-${channel}`}
                marketingChannel={original?.marketingChannel || []}
                prevAuctions={original.previousAuctions || []}
                currPrice={original.potential?.base?.amount}
                potential={original.previousAuctions?.map((amount) => amount.price?.base as MonetaryAmount)}
              />,
            ],
            [],
          )}
        />
      );
    },
    meta: {
      isNumeric: true,
      tableCellProps: {
        style: highlightCellStyle,
        onClick: (event) => event.stopPropagation(),
      },
      conditionalFormat: ConditionalFormattingType.PotentialBase,
      // FIXME: remove sortKey alias @BE
      sortKey: 'priceOrientation',
      isCustomerModeHidden: true,
      fallback: () => <NumericCellFallback textWidth="80%" supportTextWidth="4ch" />,
      export: {
        exports: [
          {
            key: 'potential.base',
            label: 'carsTableNext.car.potential.base',
          },
          {
            key: 'priceFixed',
            label: 'carsTableNext.car.priceFixed',
          },
          {
            key: 'priceOrientation',
            label: 'carsTableNext.car.priceOrientation',
          },
        ],
      },
    },
    enableSorting: true,
  },
  createPotentialColumn(
    Potential.purchaseNetWholesaleUnrepaired,
    {
      columnVisible: true,
      conditionalFormat: ConditionalFormattingType.PurchaseNetUnrepaired,
      isCustomerModeHidden: true,
    },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.purchaseNetRetailRepaired,
    {
      columnVisible: true,
      conditionalFormat: ConditionalFormattingType.PurchaseNetRepaired,
      isCustomerModeHidden: true,
    },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.purchaseGross,
    {
      columnVisible: true,
      tableCellProps: {
        style: {
          fontWeight: 'bold',
          background: lighten(0.005, grey[50]),
        },
      },
      conditionalFormat: ConditionalFormattingType.PurchaseGross,
      isTableHeaderHighlighted: true,
      filter: ['purchasePriceGross'],
      isCustomerModeHidden: true,
    },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.customerGrossPrice,
    {
      columnVisible: false,
      tableCellProps: {
        style: {
          fontWeight: 'bold',
          background: lighten(0.005, grey[50]),
        },
      },
      isTableHeaderHighlighted: true,
      isCustomerModeVisible: true,
      onlyForTable: 'cars',
      export: { enabled: false },
    },
    enableSorting,
  ),
  {
    id: 'recommendedRetailPrice',
    accessorKey: 'recommendedRetailPrice',
    cell: (props) => (
      <NumericCell
        value={props.getValue<CarsTableNextItem['recommendedRetailPrice']>() ?? '-'}
        format={currencyFormat.format}
      />
    ),
    meta: {
      isNumeric: true,
      columnVisible: false,
      isCustomerModeHidden: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
    },
    enableSorting,
  },
  {
    id: 'recommendedRetailPriceDeductionAbsolute',
    accessorKey: 'recommendedRetailPriceDeductionAbsolute',
    cell: ({ row: { original } }) => (
      <NumericCell value={original.recommendedRetailPriceDeductionAbsolute ?? '-'} format={currencyFormat.format} />
    ),
    meta: {
      sortKey: 'recommendedRetailPriceDeductionAbsolute',
      isNumeric: true,
      columnVisible: false,
      isCustomerModeHidden: true,
      fallback: () => <NumericCellFallback textWidth="80%" supportTextWidth="4ch" />,
    },
    enableSorting,
  },
  {
    id: 'recommendedRetailPriceDeductionRelative',
    accessorKey: 'recommendedRetailPriceDeductionRelative',
    cell: ({ row: { original } }) => (
      <NumericCell
        value={
          original.recommendedRetailPriceDeductionRelative
            ? percentFormat.format(original.recommendedRetailPriceDeductionRelative)
            : '-'
        }
        format={currencyFormat.format}
      />
    ),
    meta: {
      sortKey: 'recommendedRetailPriceDeductionRelative',
      isNumeric: true,
      columnVisible: false,
      isCustomerModeHidden: true,
      fallback: () => <NumericCellFallback textWidth="80%" supportTextWidth="4ch" />,
    },
    enableSorting,
  },
  {
    id: 'priceChangeAbsolute',
    accessorKey: 'priceChange.absolute',
    cell: ({ row: { original } }) => <PriceChangeAbsoluteCell original={original} />,
    meta: {
      sortKey: 'priceChangeAbsolute',
      isNumeric: true,
      columnVisible: false,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['priceChangeAbsolute', 'priceChangeRelative'],
      export: { key: 'priceChange.absolute' },
    },
    enableSorting,
    size: ColumnSize.md,
  },
  {
    id: 'priceChangeRelative',
    accessorKey: 'priceChange.relative',
    cell: ({ row: { original } }) => <PriceChangeRelativeCell original={original} />,
    meta: {
      sortKey: 'priceChangeRelative',
      isNumeric: true,
      columnVisible: false,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      filter: ['priceChangeAbsolute', 'priceChangeRelative'],
      export: { key: 'priceChange.relative' },
    },
    enableSorting,
  },
  ...externalColumns(enableSorting),
];

export const columnsWithBids: StrictColumnDef<CarsTableNextItem | BidsTableNextItem>[] = [
  ...columns(false).slice(0, 9),
  ...bidColumns(),
  ...columns(false).slice(9),
];
